import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SelectOptionItem } from "../../lib/models/ui";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export function TypeableCombobox<T extends string>({
  required,
  dwimOnChange,
  id,
  placeholder,
  className,
  options,
  value,
  onValueChange,
  disabled,
  isModal,
  tabIndex,
  popoverClassName,
  virtualizeList,
  autofocus
}: {
  required?: boolean;
  dwimOnChange?: boolean;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  options: SelectOptionItem<T>[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  isModal?: boolean;
  tabIndex?: number;
  popoverClassName?: string;
  virtualizeList?: boolean;
  autofocus?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [inputValue, setInputValue] = React.useState(value);
  const parentRef = React.useRef<HTMLDivElement>(null);

  // Sync input value when external value changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredOptions = React.useMemo(() => {
    return options.filter((option) => {
      if (!search?.trim()) return true;
      return (
        option.label
          ?.toLocaleLowerCase()
          ?.trim()
          ?.includes(search?.toLocaleLowerCase()?.trim()) || false
      );
    });
  }, [options, search]);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 10,
  });

  // Force recalculate when dropdown opens
  React.useEffect(() => {
    if (open && virtualizer) {
      const timer = setTimeout(() => {
        virtualizer.measure();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open, virtualizer]);

  // Handle input value changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onValueChange(newValue);
  };

  // Handle dropdown selection
  const handleDropdownSelect = (currentValue: string) => {
    let finalValue = currentValue;
    
    if (dwimOnChange) {
      const correctedValue = options.find((item) => 
        item.value?.toLocaleLowerCase() === currentValue?.toLocaleLowerCase()
      )?.value || "";
      finalValue = (currentValue === value || correctedValue === value) ? "" : correctedValue;
    } else {
      finalValue = currentValue === value ? "" : currentValue;
    }
    
    setInputValue(finalValue);
    onValueChange(finalValue);
    setOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <div className="flex w-full">
        <Input
          id={id}
          required={required}
          disabled={disabled}
          tabIndex={tabIndex}
          autoFocus={autofocus}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder || ""}
          className="rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input"
        />
        <Popover open={open} onOpenChange={setOpen} modal={isModal}>
          <PopoverTrigger asChild>
            <Button
              disabled={disabled}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="h-10 w-10 rounded-l-none border-l-0 px-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn("w-[200px] z-[9999] max-h-52 p-0 overflow-y-scroll", popoverClassName)}
          >
            <Command shouldFilter={false}>
              <CommandInput
                required={required}
                placeholder=""
                value={search}
                onValueChange={setSearch}
              />
              {filteredOptions.length === 0 ? (
                <CommandEmpty>No results found.</CommandEmpty>
              ) : (
                virtualizeList ? (
                  <div
                    ref={parentRef}
                    className="overflow-auto overflow-y-scroll"
                    style={{ height: "200px" }}
                  >
                    <div
                      style={{
                        height: `${virtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {virtualizer.getVirtualItems().map((virtualRow) => {
                        const option = filteredOptions[virtualRow.index];
                        return (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                            onSelect={handleDropdownSelect}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                value === option.value ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {option.label}
                          </CommandItem>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={handleDropdownSelect}
                      >
                        <Check
                          key={`check_${option.value}`}
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === option.value ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
              )}
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}